/**
 * React Query hooks for Project Fee Assumptions (9.9d CONTRACT-03)
 *
 * Provides CRUD operations and reordering for per-project fee assumption items.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ProjectFeeAssumptionCreate,
  ProjectFeeAssumptionUpdate,
  ProjectFeeAssumptionReorderItem,
} from '@/lib/schemas';
import { toast } from 'sonner';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all fee assumptions for a project
 */
export function useProjectFeeAssumptions(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-fee-assumptions', projectId],
    queryFn: () => api.projectFeeAssumptions.list(projectId!),
    enabled: !!projectId,
  });
}

/**
 * Fetch a single fee assumption
 */
export function useProjectFeeAssumption(
  projectId: string | undefined,
  assumptionId: string | undefined
) {
  return useQuery({
    queryKey: ['project-fee-assumptions', projectId, assumptionId],
    queryFn: () => api.projectFeeAssumptions.get(projectId!, assumptionId!),
    enabled: !!projectId && !!assumptionId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new fee assumption
 */
export function useCreateProjectFeeAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: ProjectFeeAssumptionCreate;
    }) => api.projectFeeAssumptions.create(projectId, data),
    onSuccess: (response, variables) => {
      const newAssumption = response?.fee_assumption || response;
      // Update cache directly from response — immediate UI update
      queryClient.setQueryData<any>(
        ['project-fee-assumptions', variables.projectId],
        (old) => {
          if (!old?.fee_assumptions) return old;
          return {
            ...old,
            fee_assumptions: [...old.fee_assumptions, newAssumption],
            total: (old.total ?? 0) + 1,
          };
        }
      );
      // Don't invalidate — we just set that cache.
      toast.success('Fee assumption added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add fee assumption');
    },
  });
}

/**
 * Update a fee assumption
 */
export function useUpdateProjectFeeAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      assumptionId,
      data,
    }: {
      projectId: string;
      assumptionId: string;
      data: ProjectFeeAssumptionUpdate;
    }) => api.projectFeeAssumptions.update(projectId, assumptionId, data),
    onSuccess: (response, variables) => {
      const updatedAssumption = response?.fee_assumption || response;
      // Update cache directly from response — immediate UI update
      queryClient.setQueryData<any>(
        ['project-fee-assumptions', variables.projectId],
        (old) => {
          if (!old?.fee_assumptions) return old;
          return {
            ...old,
            fee_assumptions: old.fee_assumptions.map((item: any) =>
              item.id === variables.assumptionId ? { ...item, ...updatedAssumption } : item
            ),
          };
        }
      );
      // NOTE: Do NOT invalidateQueries here — we just set the cache from the server
      // response. Invalidating would trigger a refetch that can overwrite this
      // correct data with a stale Cloud Run read (the "self-overwriting" bug).
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update fee assumption');
    },
  });
}

/**
 * Delete a fee assumption
 */
export function useDeleteProjectFeeAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      assumptionId,
    }: {
      projectId: string;
      assumptionId: string;
    }) => api.projectFeeAssumptions.delete(projectId, assumptionId),
    onMutate: async ({ projectId, assumptionId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['project-fee-assumptions', projectId],
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData([
        'project-fee-assumptions',
        projectId,
      ]);

      // Optimistically update
      queryClient.setQueryData<any>(
        ['project-fee-assumptions', projectId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            fee_assumptions: old.fee_assumptions.filter(
              (item: any) => item.id !== assumptionId
            ),
            total: old.total - 1,
          };
        }
      );

      return { previousData, projectId };
    },
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ['project-fee-assumptions', context.projectId],
          context.previousData
        );
      }
      toast.error(error.message || 'Failed to delete fee assumption');
    },
    onSuccess: () => {
      toast.success('Fee assumption deleted');
    },
  });
}

/**
 * Reorder fee assumptions (drag-drop)
 */
export function useReorderProjectFeeAssumptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      items,
    }: {
      projectId: string;
      items: ProjectFeeAssumptionReorderItem[];
    }) => api.projectFeeAssumptions.reorder(projectId, items),
    onSuccess: (response, variables) => {
      // Update cache with new order from server
      queryClient.setQueryData(
        ['project-fee-assumptions', variables.projectId],
        response
      );
      toast.success('Order updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reorder fee assumptions');
    },
  });
}
